/**
 * AI Copilot Service — client for the Radiology Intelligence API.
 */
import apiClient from './apiClient';
import { API_URL } from '../config';
import { store } from '../store';
import { logout } from '../store/authSlice';

export interface AIQueryContext {
  patient_id?: string;
  study_id?: number;
  session_id?: string;
}

export interface AIQueryRequest {
  query: string;
  context?: AIQueryContext;
  conversation_id?: string;
}

export interface SourceReference {
  source_type: string;
  source_id: string;
  label: string;
  metadata: Record<string, unknown>;
}

export interface ReasoningStep {
  name: string;
  action: string;
  success: boolean;
  latency_ms: number;
  error?: string | null;
}

export interface AIQueryResponse {
  answer: string;
  sources: SourceReference[];
  confidence: number;
  intent: string;
  follow_up_suggestions: string[];
  reasoning_steps: ReasoningStep[];
  metadata: Record<string, unknown>;
}

export interface ConversationTurn {
  query: string;
  response: string | null;
  intent: string | null;
  confidence: number | null;
  sources: Record<string, unknown>[] | null;
  latency_ms: number | null;
  created_at: string;
}

export interface ConversationHistory {
  conversation_id: string;
  turns: ConversationTurn[];
  total: number;
}

const submitQuery = async (request: AIQueryRequest): Promise<AIQueryResponse> => {
  const response = await apiClient.post(`${API_URL}/ai/query`, request);
  return response.data;
};

export interface AIStreamEvent {
  event: string;
  step?: string;
  steps?: { name: string; action: string }[];
  action?: string;
  success?: boolean;
  latency_ms?: number;
  error?: string;
  output_summary?: string;
  answer?: string;
  metadata?: Record<string, unknown>;
  response?: AIQueryResponse;
}

const streamQuery = (
  request: AIQueryRequest,
  onEvent: (event: AIStreamEvent) => void,
  onError?: (error: string) => void
): { close: () => void } => {
  const abortController = new AbortController();

  const start = async () => {
    try {
      const token = store.getState().auth.token;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/ai/query/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (response.status === 401) {
        store.dispatch(logout());
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        if (onError) onError('Session expired. Please log in again.');
        return;
      }

      if (!response.ok) {
        const text = await response.text().catch(() => 'Streaming request failed');
        if (onError) onError(`Streaming error ${response.status}: ${text}`);
        return;
      }

      if (!response.body) {
        if (onError) onError('No response body from stream');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventName = 'message';
      let eventData = '';
      let receivedTerminalEvent = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventName = line.replace('event:', '').trim();
          } else if (line.startsWith('data:')) {
            eventData = line.replace('data:', '').trim();
          } else if (line === '' && eventData) {
            try {
              const data = JSON.parse(eventData) as AIStreamEvent;
              data.event = eventName;
              onEvent(data);
              if (eventName === 'complete' || eventName === 'error') {
                receivedTerminalEvent = true;
                abortController.abort();
                return;
              }
            } catch {
              // Ignore malformed events
            }
            eventName = 'message';
            eventData = '';
          }
        }
      }

      if (!receivedTerminalEvent && onError) {
        onError('Stream ended before a final response was received.');
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return;
      if (onError) onError((err as Error).message || 'Streaming connection failed');
    }
  };

  start();

  return { close: () => abortController.abort() };
};

const getConversationHistory = async (conversationId: string): Promise<ConversationHistory> => {
  const response = await apiClient.get(`${API_URL}/ai/conversations/${conversationId}`);
  return response.data;
};

const bulkIndex = async (): Promise<{ reports: number; transcriptions: number; history_entries: number }> => {
  const response = await apiClient.post(`${API_URL}/ai/index/bulk`);
  return response.data;
};

const aiCopilotService = {
  submitQuery,
  streamQuery,
  getConversationHistory,
  bulkIndex,
};

export default aiCopilotService;
